import { useState } from "react";
import { Link } from "react-router-dom";
import { Show } from "@clerk/react";
import { Menu } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { SmartLink } from "@/components/landing/HashLink";
import { ProfileMenu, useLogout } from "@/components/ProfileMenu";
import { useMe } from "@/context/MeContext";
import { LayoutDashboard, LogOut, UserRound } from "lucide-react";

const MobileAccount = ({ close }) => {
  const { me } = useMe();
  const logout = useLogout();
  const admin = !!me?.user?.is_admin;
  const cls = "flex h-12 items-center gap-3 rounded-lg px-3 text-base font-medium text-slate-800 hover:bg-slate-100";
  return (
    <div className="flex flex-col gap-1 border-t border-slate-200 pt-4">
      {me && <p className="break-all px-3 pb-2 text-sm text-slate-600"><span className="block font-semibold text-slate-900">{me.user.name}</span>{me.user.email}</p>}
      <Link to={admin ? "/admin" : "/"} onClick={close} className={cls} data-testid="mobile-dashboard-link"><LayoutDashboard className="h-5 w-5" /> {admin ? "Admin" : "My dashboard"}</Link>
      <Link to="/account" onClick={close} className={cls} data-testid="mobile-account-link"><UserRound className="h-5 w-5" /> Account and security</Link>
      <button onClick={() => { close(); logout(); }} className={`${cls} mt-2 border-t border-slate-100 text-rose-700 hover:bg-rose-50`} data-testid="mobile-logout-btn"><LogOut className="h-5 w-5" /> Log out</button>
    </div>
  );
};

export const NAV_LINKS = [
  { label: "Courses", to: "#courses", id: "courses" },
  { label: "How it works", to: "#how-it-works", id: "how-it-works" },
  { label: "For Organisations", to: "#organisations", id: "for-organisations" },
  { label: "Help", to: "/help", id: "help" },
];

const AuthButtons = ({ prefix, full, close }) => (
  <>
    <Show when="signed-in">
      {full ? <MobileAccount close={close} /> : <ProfileMenu testid={`${prefix}-profile-menu`} />}
    </Show>
    <Show when="signed-out">
      <Button asChild variant="ghost" className={full ? "w-full" : ""} data-testid={`${prefix}-signin-btn`}><Link to="/sign-in">Sign in</Link></Button>
      <Button asChild className={`rounded-full bg-[#222d99] px-5 hover:bg-[#1a237e] ${full ? "w-full" : ""}`} data-testid={`${prefix}-signup-btn`}><Link to="/sign-up">Get started</Link></Button>
    </Show>
  </>
);

export function SiteHeader() {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/90 backdrop-blur-md">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
        <Logo />
        <nav className="hidden items-center gap-1 md:flex" aria-label="Main" data-testid="site-nav">
          {NAV_LINKS.map((l) => (
            <SmartLink key={l.id} to={l.to} className="rounded-md px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100 hover:text-slate-950" data-testid={`site-nav-${l.id}`}>{l.label}</SmartLink>
          ))}
        </nav>
        <div className="hidden items-center gap-2 md:flex"><AuthButtons prefix="landing" /></div>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button className="rounded-md p-2 text-slate-800 hover:bg-slate-100 md:hidden" aria-label="Open menu" data-testid="site-mobile-menu-btn"><Menu className="h-5 w-5" /></button>
          </SheetTrigger>
          <SheetContent side="right" className="w-72 p-6">
            <SheetTitle className="sr-only">Menu</SheetTitle>
            <Logo />
            <nav className="mt-8 flex flex-col gap-1" aria-label="Mobile">
              {NAV_LINKS.map((l) => (
                <SmartLink key={l.id} to={l.to} onClick={() => setOpen(false)} className="rounded-lg px-3 py-3 text-base font-medium text-slate-800 hover:bg-slate-100" data-testid={`site-mobile-nav-${l.id}`}>{l.label}</SmartLink>
              ))}
            </nav>
            <div className="mt-8 flex flex-col gap-2"><AuthButtons prefix="mobile" full close={() => setOpen(false)} /></div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
